import { useState, useMemo } from 'react'
import { API } from 'aws-amplify'
import dayjs from 'dayjs';
import { Paper, SvgIcon, IconButton, CircularProgress, Dialog, DialogContent, DialogActions, DialogTitle, DialogContentText, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGridPremium, GridColDef, useGridApiRef, GridRowParams } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../common/Components/PageHeader';
import { Status, useFetch } from '../../hooks/useFetch';
import { useParams } from 'react-router-dom';
import { LoadingPlanGroupFields, AirlineFields, AirportFields, DestinationGroupFields, LoadingPlanFields, HaulTypeFields, CatererFields, ServiceTypeFields } from '@aviation/catering-masterdata-sdk';
import { DynamoDbObject } from '@aviation/catering-common';
import StyledBox from '../../common/Components/StyledBox'
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ulid } from 'ulidx';
import {toast} from "react-toastify";
import LoadingPlanGroupDialog, { ILoadingPlanGroupDialogItem } from './LoadingPlanGroupDialog';

function LoadingPlanGroups() {
    const apiRef = useGridApiRef();
    const { t } = useTranslation();
    const { clientCode } = useParams();
    const { status, data = [] } = useFetch<Array<LoadingPlanGroupFields & DynamoDbObject>>(`/api/masterdata/loadingplangroup/${clientCode}`);

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { status: airlineStatus, data: airlineData = [] } = useFetch<Array<AirlineFields & DynamoDbObject>>(`/api/masterdata/airline/${clientCode}`);
    const { status: airportStatus, data: airportData = [] } = useFetch<Array<AirportFields & DynamoDbObject>>(`/api/masterdata/airport/`);
    const { status: destinationGroupStatus, data: destinationGroupData = [] } = useFetch<Array<DestinationGroupFields & DynamoDbObject>>(`/api/masterdata/destinationgroup/${clientCode}`);
    const { status: loadingPlanStatus, data: loadingPlanData = [] } = useFetch<Array<LoadingPlanFields & DynamoDbObject>>(`/api/masterdata/loadingplan/${clientCode}`);
    const { status: haulTypeStatus, data: haulTypeData = [] } = useFetch<Array<HaulTypeFields & DynamoDbObject>>(`/api/masterdata/haultype/${clientCode}`);
    const { status: catererStatus, data: catererData = [] } = useFetch<Array<CatererFields & DynamoDbObject>>(`/api/masterdata/caterer`);
    const { status: serviceTypeStatus, data: serviceTypeData = [] } = useFetch<Array<ServiceTypeFields & DynamoDbObject>>(`/api/masterdata/servicetype/${clientCode}`);

    const isLoadingReferenceData = [airlineStatus, airportStatus, destinationGroupStatus, loadingPlanStatus, haulTypeStatus, catererStatus, serviceTypeStatus]
        .some(s => s === Status.Fetching || s === Status.Idle);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteItem, setDeleteItem] = useState<LoadingPlanGroupFields & DynamoDbObject | undefined>(undefined);
    const [showDialog, setShowDialog] = useState(false);
    const [editItem, setEditItem] = useState<(ILoadingPlanGroupDialogItem & { PK: string, RK: string }) | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    const isDataReady = status === Status.Fetched && !isLoadingReferenceData;
    const isLoading = !isDataReady || loading;

    const rowId = (row : any) : string => {
        return `${row.PK}#${row.RK}`;
    }

    const isRowValid = (row : any) : boolean => {
        const today = dayjs();

        if(row.ValidFrom && dayjs(row.ValidFrom).isAfter(today, 'day'))
            return false;
        if(row.ValidTo && dayjs(row.ValidTo).isBefore(today, 'day'))
            return false;
        return true;
    }

    const rows = useMemo(() => {
        return data.map((o) => {
            const keys = o.RK.split('#');
            return {
                ...o,
                id: rowId(o),
                Al: keys[0],
                Dep: o.Dep ?? keys[1],
                FlightNos: (o.FlightNos ?? []).join(', '),
            };
        }).sort((a,b) => (a.Al ?? '') < (b.Al ?? '') ? -1 : 1);
    }, [data]);

    const airlineList = useMemo(() => {
        return airlineData.map(o => { return {label: (o as any).Description ?? '', value: o.RK } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [airlineData])

    const departureStationList = useMemo(() => {
        let result : any[] = [];

        if(airlineData !== undefined) {
            // Build unique list of departure stations for each airline
            airlineData.forEach(a => {
                const list = new Set(airportData.filter(o => o.RK.split('#')[0] === a.RK).map(o => o.RK.split('#')[1]));
                result = result.concat(...Array.from(list).map(o => { return { label: o, value: o, airline: a.RK }}).sort((a,b) => (a.label ?? '') < (b.label ?? '') ? -1 : 1));
            });
        }

        return result;
    }, [airportData, airlineData])

    const destinationGroupList = useMemo(() => {
        return destinationGroupData.map(o => { return {label: o.Name ?? '', value: o.RK.split('#')[1], airline: o.RK.split('#')[0] } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [destinationGroupData])

    const loadingPlanList = useMemo(() => {
        return loadingPlanData.map(o => { return {label: o.Name ?? '', value: o.RK.split('#')[1], airline: o.RK.split('#')[0] } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [loadingPlanData])

    const haulTypeList = useMemo(() => {
        return haulTypeData.map(o => { return {label: o.Name ?? '', value: o.RK.split('#')[1], airline: o.RK.split('#')[0] } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [haulTypeData])

    const catererList = useMemo(() => {
        return catererData.map(o => { return {label: o.Name ?? '', value: o.RK } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [catererData])

    const loadingTypeList = useMemo(() => {
        let result : any[] = [];

        if(airlineData !== undefined) {
            // Build unique list of loading types for each airline
            airlineData.forEach(a => {
                const list = new Set(serviceTypeData.filter(t => t.RK.split('#')[0] === a.RK).map(t => t.Type === 'CREW' ? t.Type : t.Code));
                result = result.concat(...Array.from(list).map(o => { return { label: o, value: o, airline: a.RK }}).sort((a,b) => (a.label ?? '') < (b.label ?? '') ? -1 : 1));
            });
        }

        return result;
    }, [serviceTypeData, airlineData])

    const waveList = useMemo(() => {
        return [
            {label: "W1", value: 1 },
            {label: "W2", value: 2 },
            {label: "W3", value: 3 }]
    }, []);

    const columns: GridColDef[] = [
        { field: 'actions', type: 'actions', headerName: '', minWidth: 80, renderCell: ({ row }: Partial<GridRowParams>) => {
            const isRowLoading = row.IsLoading ?? false;

            if(isRowLoading)
                return (
                    <CircularProgress size={19} />
                )

            return [
                <IconButton color="primary" onClick={() => { openEditDialog(row); }}>
                    <SvgIcon component={EditIcon} inheritViewBox />
                </IconButton>,
                <IconButton color="error" onClick={() => confirmDeletion(row)}>
                    <SvgIcon component={TrashIcon} inheritViewBox />
                </IconButton>
            ]
            }
        },
        { field: 'Al', headerName: 'Airline', minWidth: 120,
            type: 'singleSelect', valueOptions: airlineList },
        { field: 'Dep', headerName: 'Departure Station', minWidth: 150 },
        { field: 'DestinationGroupId', headerName: 'Destination Group', minWidth: 170,
            type: 'singleSelect', valueOptions: destinationGroupList },
        { field: 'LoadingPlanId', headerName: 'Loading Plan', minWidth: 150,
            type: 'singleSelect', valueOptions: (params) => loadingPlanList.filter(o => o.airline === params.row.Al) },
        { field: 'HaulType', headerName: 'Haul Type', minWidth: 120,
            type: 'singleSelect', valueOptions: (params) => haulTypeList.filter(o => o.airline === params.row.Al) },
        { field: 'CatererId', headerName: 'Caterer', minWidth: 150,
            type: 'singleSelect', valueOptions: catererList },
        { field: 'LoadingTypes', headerName: 'Loading Types', minWidth: 200,
            renderCell: (params) => (params.value ?? []).join(', ') },
        { field: 'Waves', headerName: 'Waves', minWidth: 150,
            renderCell: (params) => {
                const selected = params.value ?? [];
                return waveList.filter(o => selected.indexOf(o.value) > -1).map(o => o.label).join(', ');
            } },
        { field: 'FlightNos', headerName: 'Flight No.', minWidth: 150 },
        { field: 'IsDutyFree', headerName: 'Duty Free', minWidth: 90, type: 'boolean' }
    ];

    const handleCloseDialog = () => {
        setDeleteItem(undefined);
        setShowDeleteConfirmation(false);
    };

    const handleDeleteItem = async () => {
        setShowDeleteConfirmation(false);

        if(deleteItem !== undefined) {
            apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: true }]);

            const init = {
                body: {},
                headers: {}
            };

            try {
                const keys = deleteItem.RK.split('#');
                await API.del('api', `/api/masterdata/loadingplangroup/${clientCode}/${keys[0]}/${keys[1]}/${keys[2]}`, init);
                apiRef.current.updateRows([{ id: rowId(deleteItem), _action: 'delete' }]);
                toast.success(`Loading plan group deleted successfully.`);
                return true;
            } catch(e) {
                console.error(e);
                apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: false }]);
                toast.error(`An error occurred while deleting a loading plan group`);
                return false;
            } finally {
                setDeleteItem(undefined);
            }
        }
    };

    const updateItem = async(item : LoadingPlanGroupFields & DynamoDbObject) => {
        const init = {
            body: item,
            headers: {}
        };

        try {
            const keys = item.RK.split('#');
            await API.put('api', `/api/masterdata/loadingplangroup/${clientCode}/${keys[0]}/${keys[1]}/${keys[2]}`, init);
            return true;
        } catch(e) {
            console.error(e);
            return false;
        }
    }

    const confirmDeletion = (item : any) => {
        setDeleteItem(item);
        setShowDeleteConfirmation(true);
    };

    const openEditDialog = (row : any) => {
        // The grid row holds FlightNos as a joined display string; the dialog needs the raw array back.
        setEditItem({
            ...row,
            FlightNos: typeof row.FlightNos === 'string'
                ? row.FlightNos.split(',').map((o : string) => o.trim()).filter((o : string) => o !== '')
                : (row.FlightNos ?? []),
        });
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
        setEditItem(undefined);
    }

    const onSaveItem = async (airline : string, dep : string, destinationGroupId : string, loadingPlanId : string, haulType : string, catererId : string, isDutyFree : boolean, flightNos : string[], excludeFlightNos : boolean, loadingTypes : string[], waves : number[], validFrom : string | undefined, validTo : string | undefined) => {
        const isEdit = editItem !== undefined;
        closeDialog();
        setLoading(true);

        const item : LoadingPlanGroupFields & DynamoDbObject = {
            Dep: dep,
            DestinationGroupId: destinationGroupId,
            LoadingPlanId: loadingPlanId,
            HaulType: haulType,
            CatererId: catererId,
            IsDutyFree: isDutyFree,
            FlightNos: flightNos,
            ExcludeFlightNos: excludeFlightNos,
            LoadingTypes: loadingTypes,
            Waves: waves,
            ValidFrom: validFrom,
            ValidTo: validTo,
            PK: isEdit ? editItem!.PK : `${clientCode}#LoadingPlanGroup`,
            RK: isEdit ? editItem!.RK : `${airline}#${dep}#${ulid().toString()}`,
        };

        if(await updateItem(item)) {
            setLoading(false);
            apiRef.current.updateRows([{
                ...item,
                id: rowId(item),
                Al: airline,
                FlightNos: flightNos.join(', '),
            }]);
            toast.success(isEdit ? `Loading Plan Group updated successfully` : `Loading Plan Group added successfully`);
        } else {
            setLoading(false);
            toast.error(isEdit ? `An error occurred while updating loading plan group` : `An error occurred while creating loading plan group`);
        }
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <PageHeader title={t('Loading Plan Groups')}>
                    <Button variant="contained" onClick={() => { setEditItem(undefined); setShowDialog(true); }}>Create</Button>
                </PageHeader>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '100px' }} elevation={3}>
                    <StyledBox>
                        <DataGridPremium
                            apiRef={apiRef}
                            autoHeight
                            rows={isDataReady ? rows : []}
                            columns={columns}

                            getRowClassName={(params) => {
                                const classes = params.indexRelativeToCurrentPage % 2 === 1 ? ['tui-grid-alternate-row'] : [];
                                if(!isRowValid(params.row))
                                    classes.push('tui-grid-row-invalid');
                                return classes.join(' ');
                            }}
                            pagination
                            loading={isLoading}
                        />
                    </StyledBox>
                    { showDialog && (
                        <LoadingPlanGroupDialog
                            airlineList={airlineList}
                            departureStationList={departureStationList}
                            destinationGroupList={destinationGroupList}
                            loadingPlanList={loadingPlanList}
                            haulTypeList={haulTypeList}
                            catererList={catererList}
                            loadingTypeList={loadingTypeList}
                            waveList={waveList}
                            open={showDialog}
                            editItem={editItem}
                            cancelAction={closeDialog}
                            saveAction={onSaveItem} />
                    )}
                    <Dialog open={showDeleteConfirmation} onClose={handleCloseDialog}>
                        <DialogTitle id="alert-dialog-title">
                            {"Delete Loading Plan Group?"}
                        </DialogTitle>
                        <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete this loading plan group? You can't undo this action.
                        </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} autoFocus>Cancel</Button>
                            <Button onClick={handleDeleteItem} color="error">Delete</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default LoadingPlanGroups;
